/**
 * LAS CANCHAS: dónde se puede ir a jugar, cuánto cobran y a qué hora abren.
 *
 * LA CARGA CUALQUIERA Y LA CORRIGE CUALQUIERA. Por eso el botón de editar
 * está en TODAS las fichas y no solo en las propias: una tarifa de hace un año
 * la corrige quien fue ayer, y esa persona casi nunca es la que cargó la
 * ficha. Debajo de cada una dice quién la tocó de último, que es a quien hay
 * que preguntarle si algo no cuadra.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { COURT_LIMITS } from '@tennisup/contracts';
import type { Court, SaveCourtRequest } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { Field } from '../components/Field';
import { GeoPicker, type GeoValue } from '../components/GeoPicker';
import { api } from '../lib/api';
import { errorMessage, errorSummary } from '../lib/errorMessage';
import { whatsappHref } from '../lib/whatsapp';

const SIN_ZONA: GeoValue = { provinceCode: '', cantonCode: '', districtCode: '' };

interface Formulario {
  name: string;
  addressNote: string;
  description: string;
  zona: GeoValue;
  priceWithLight: string;
  priceWithoutLight: string;
  phone: string;
  websiteUrl: string;
  opensAt: string;
  closesAt: string;
}

const VACÍO: Formulario = {
  name: '',
  addressNote: '',
  description: '',
  zona: SIN_ZONA,
  priceWithLight: '',
  priceWithoutLight: '',
  phone: '',
  websiteUrl: '',
  opensAt: '',
  closesAt: '',
};

export function CourtsPage(): JSX.Element {
  const { t } = useTranslation();
  const [canchas, setCanchas] = useState<Court[]>([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filtro, setFiltro] = useState<GeoValue>(SIN_ZONA);
  const [texto, setTexto] = useState('');

  /** `null` = el formulario está cerrado. Una cancha = se está editando ésa. */
  const [editando, setEditando] = useState<Court | 'nueva' | null>(null);

  const buscar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      // Las claves se ponen solo si tienen valor: con
      // `exactOptionalPropertyTypes`, `undefined` explícito no es lo mismo que
      // ausente, y `CourtQuery` las declara opcionales, no nulables.
      const pagina = await api.listCourts({
        ...(filtro.provinceCode === '' ? {} : { provinceCode: filtro.provinceCode }),
        ...(filtro.cantonCode === '' ? {} : { cantonCode: filtro.cantonCode }),
        ...(texto.trim() === '' ? {} : { text: texto.trim() }),
      });
      setCanchas(pagina.items);
      setTotal(pagina.total);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setCargando(false);
    }
  }, [filtro.provinceCode, filtro.cantonCode, texto, t]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  if (editando !== null) {
    return (
      <CourtForm
        cancha={editando === 'nueva' ? null : editando}
        onDone={() => {
          setEditando(null);
          void buscar();
        }}
        onCancel={() => setEditando(null)}
      />
    );
  }

  return (
    <AppLayout>
      {/* Las canchas se abren desde «Más», que es donde vive el enlace. */}
      <BackButton to="/mas" />
      <h1>{t('courts.title')}</h1>
      <p className="help">{t('courts.lead')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <button type="button" onClick={() => setEditando('nueva')}>
        {t('courts.add')}
      </button>

      <section className="court-filters">
        <Field id="courts-text" label={t('courts.search')}>
          {(control) => (
            <input
              {...control}
              type="search"
              autoComplete="off"
              placeholder={t('courts.searchPlaceholder')}
              value={texto}
              onChange={(event) => setTexto(event.target.value)}
            />
          )}
        </Field>
        {/* Sin distrito: para mirar una lista alcanza con el cantón, y un
            selector más es un paso más entre la persona y las canchas. */}
        <GeoPicker idPrefix="courts-filter" value={filtro} onChange={setFiltro} withDistrict={false} />
      </section>

      {cargando ? (
        <p className="help">{t('common.loading')}</p>
      ) : canchas.length === 0 ? (
        <p className="help">{t('courts.empty')}</p>
      ) : (
        <>
          <p className="help">{t('courts.count', { count: total })}</p>
          <ul className="court-list">
            {canchas.map((cancha) => (
              <CourtCard key={cancha.id} court={cancha} onEdit={() => setEditando(cancha)} />
            ))}
          </ul>
        </>
      )}
    </AppLayout>
  );
}

function CourtCard({ court, onEdit }: { court: Court; onEdit: () => void }): JSX.Element {
  const { t } = useTranslation();
  // `whatsappHref` ya devuelve `null` con un número corto o vacío, así que no
  // hace falta preguntar antes por `court.phone`: un mutante que quitaba esa
  // pregunta no rompía ninguna prueba, y tenía razón — era código de más.
  const wa = whatsappHref(court.phone ?? '');

  return (
    <li className="court-card">
      <div className="court-head">
        <strong>{court.name}</strong>
        <span className="help">{court.locationLabel}</span>
      </div>

      {court.addressNote !== null && <p className="help">{court.addressNote}</p>}

      {/* CON LOS SALTOS DE LÍNEA PUESTOS. Se guardan de varias líneas, así que
          mostrarlas en un párrafo corrido perdería justo lo que se escribió:
          `white-space: pre-line` en `.court-description` los respeta. */}
      {court.description !== null && <p className="court-description">{court.description}</p>}

      <dl className="court-data">
        {/* CERO ES GRATIS y `null` es «no sabemos»: son dos cosas distintas y
            se muestran distinto. Ver `checkPrice` en el dominio. */}
        <div>
          <dt>{t('courts.withLight')}</dt>
          <dd>{precio(t, court.priceWithLightColones)}</dd>
        </div>
        <div>
          <dt>{t('courts.withoutLight')}</dt>
          <dd>{precio(t, court.priceWithoutLightColones)}</dd>
        </div>
        <div>
          <dt>{t('courts.hours')}</dt>
          <dd>
            {court.opensAt === null || court.closesAt === null
              ? t('courts.unknown')
              : `${court.opensAt} – ${court.closesAt}`}
          </dd>
        </div>
      </dl>

      <div className="court-actions">
        {wa !== null && (
          <a className="button-like whatsapp" href={wa} target="_blank" rel="noreferrer noopener">
            {t('courts.whatsapp')}
          </a>
        )}
        {court.websiteUrl !== null && (
          <a className="link" href={court.websiteUrl} target="_blank" rel="noreferrer noopener">
            {t('courts.website')}
          </a>
        )}
        <button type="button" className="link" onClick={onEdit}>
          {t('courts.edit')}
        </button>
      </div>

      {/*
        QUIÉN LA CARGÓ, Y QUIÉN LA CORRIGIÓ DESPUÉS.
        Javier: «cuando agreguen las canchas poner el usuario que lo agregó, y
        si lo modifican también». Acá salía SOLO la última corrección, así
        que quien se tomó el trabajo de cargar la ficha desaparecía en cuanto
        otro le arreglaba una coma.

        UNA SOLA LÍNEA CUANDO ES LA MISMA PERSONA. Con las dos, una cancha
        recién cargada decía «Agregada por Ana · Última corrección: Ana», que
        es la misma frase escrita dos veces y hace dudar de si son dos
        personas distintas con el mismo apodo.
      */}
      <p className="help court-credit">
        {t('courts.addedBy', { name: court.createdByName })}
        {court.updatedByName !== court.createdByName && (
          <> · {t('courts.updatedBy', { name: court.updatedByName })}</>
        )}
      </p>
    </li>
  );
}

/** `null` no es 0: «no sabemos» y «gratis» son dos respuestas distintas. */
function precio(t: TFunction, colones: number | null): string {
  if (colones === null) return t('courts.unknown');
  if (colones === 0) return t('courts.free');
  return t('courts.perHour', { amount: colones.toLocaleString('es-CR') });
}

function CourtForm({
  cancha,
  onDone,
  onCancel,
}: {
  cancha: Court | null;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [values, setValues] = useState<Formulario>(
    cancha === null
      ? VACÍO
      : {
          name: cancha.name,
          addressNote: cancha.addressNote ?? '',
          description: cancha.description ?? '',
          zona: {
            provinceCode: cancha.provinceCode,
            cantonCode: cancha.cantonCode,
            districtCode: cancha.districtCode,
          },
          priceWithLight: cancha.priceWithLightColones?.toString() ?? '',
          priceWithoutLight: cancha.priceWithoutLightColones?.toString() ?? '',
          phone: cancha.phone ?? '',
          websiteUrl: cancha.websiteUrl ?? '',
          opensAt: cancha.opensAt ?? '',
          closesAt: cancha.closesAt ?? '',
        },
  );
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function patch(cambio: Partial<Formulario>): void {
    setValues((antes) => ({ ...antes, ...cambio }));
  }

  async function guardar(): Promise<void> {
    setEnviando(true);
    setError(null);
    setFieldErrors({});
    const cuerpo: SaveCourtRequest = {
      name: values.name,
      addressNote: values.addressNote,
      description: values.description,
      ...values.zona,
      // El campo vacío es «no sabemos» (null), no cero. Cero es gratis, y para
      // eso la persona escribe 0.
      priceWithLightColones: aColones(values.priceWithLight),
      priceWithoutLightColones: aColones(values.priceWithoutLight),
      phone: values.phone,
      websiteUrl: values.websiteUrl,
      opensAt: values.opensAt,
      closesAt: values.closesAt,
    };
    try {
      if (cancha === null) await api.createCourt(cuerpo);
      else await api.updateCourt(cancha.id, cuerpo);
      onDone();
    } catch (caught) {
      // `errorSummary` y no `errorMessage`: los mensajes por campo se pintan
      // al lado de su input, y repetirlos arriba los dice dos veces.
      setError(errorSummary(t, caught));
      const detalle = (caught as { fieldErrors?: Record<string, string> }).fieldErrors;
      if (detalle !== undefined) setFieldErrors(detalle);
      setEnviando(false);
    }
  }

  return (
    <AppLayout>
      <h1>{cancha === null ? t('courts.addTitle') : t('courts.editTitle')}</h1>
      {cancha !== null && <p className="help">{t('courts.editHelp')}</p>}

      {error !== null && <Alert kind="error">{error}</Alert>}

      <Field id="court-name" label={t('courts.name')} error={fieldErrors['name']}>
        {(control) => (
          <input
            {...control}
            type="text"
            value={values.name}
            onChange={(event) => patch({ name: event.target.value })}
          />
        )}
      </Field>

      <Field
        id="court-address"
        label={t('courts.addressNote')}
        help={t('courts.addressHelp')}
        error={fieldErrors['addressNote']}
      >
        {(control) => (
          <input
            {...control}
            type="text"
            value={values.addressNote}
            onChange={(event) => patch({ addressNote: event.target.value })}
          />
        )}
      </Field>

      {/*
        LO QUE HAY QUE SABER ANTES DE IR.

        ES UN `textarea` Y NO UN `input`, y no es cosmético: en un `input` la
        tecla Enter ENVÍA el formulario, así que quien intenta escribir la
        segunda línea termina guardando a medias. Un campo que se anuncia de
        varias líneas tiene que aceptarlas.

        VA DEBAJO DE LAS SEÑAS porque contestan la misma pregunta —«cómo es ir
        ahí»— por partes: las señas dicen dónde queda, esto dice con qué se va
        a encontrar.
      */}
      <Field
        id="court-description"
        label={t('courts.description')}
        help={t('courts.descriptionHelp')}
        error={fieldErrors['description']}
      >
        {(control) => (
          <textarea
            {...control}
            rows={4}
            maxLength={COURT_LIMITS.descriptionMax}
            value={values.description}
            onChange={(event) => patch({ description: event.target.value })}
          />
        )}
      </Field>

      <GeoPicker
        idPrefix="court"
        value={values.zona}
        onChange={(zona) => patch({ zona })}
        fieldErrors={fieldErrors}
      />

      <div className="row">
        <Field
          id="court-price-light"
          label={t('courts.withLight')}
          help={t('courts.priceHelp')}
          error={fieldErrors['priceWithLightColones']}
        >
          {(control) => (
            <input
              {...control}
              type="number"
              inputMode="numeric"
              min={0}
              value={values.priceWithLight}
              onChange={(event) => patch({ priceWithLight: event.target.value })}
            />
          )}
        </Field>

        <Field
          id="court-price-no-light"
          label={t('courts.withoutLight')}
          error={fieldErrors['priceWithoutLightColones']}
        >
          {(control) => (
            <input
              {...control}
              type="number"
              inputMode="numeric"
              min={0}
              value={values.priceWithoutLight}
              onChange={(event) => patch({ priceWithoutLight: event.target.value })}
            />
          )}
        </Field>
      </div>

      <div className="row">
        <Field id="court-opens" label={t('courts.opensAt')} error={fieldErrors['opensAt']}>
          {(control) => (
            <input
              {...control}
              type="time"
              value={values.opensAt}
              onChange={(event) => patch({ opensAt: event.target.value })}
            />
          )}
        </Field>
        <Field
          id="court-closes"
          label={t('courts.closesAt')}
          help={t('courts.hoursHelp')}
          error={fieldErrors['closesAt']}
        >
          {(control) => (
            <input
              {...control}
              type="time"
              value={values.closesAt}
              onChange={(event) => patch({ closesAt: event.target.value })}
            />
          )}
        </Field>
      </div>

      <Field
        id="court-phone"
        label={t('courts.phone')}
        help={t('courts.phoneHelp')}
        error={fieldErrors['phone']}
      >
        {(control) => (
          <input
            {...control}
            type="tel"
            value={values.phone}
            onChange={(event) => patch({ phone: event.target.value })}
          />
        )}
      </Field>

      <Field id="court-web" label={t('courts.website')} error={fieldErrors['websiteUrl']}>
        {(control) => (
          <input
            {...control}
            type="url"
            placeholder="https://"
            value={values.websiteUrl}
            onChange={(event) => patch({ websiteUrl: event.target.value })}
          />
        )}
      </Field>

      <div className="button-row">
        <button type="button" disabled={enviando} onClick={() => void guardar()}>
          {t('courts.save')}
        </button>
        <button type="button" className="link" disabled={enviando} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </AppLayout>
  );
}

/**
 * El campo vacío es `null` —«no sabemos»— y NO cero.
 *
 * Cero es gratis, que es un dato real, y para eso la persona escribe un 0.
 * Mandar 0 por un campo en blanco haría que toda cancha sin tarifa cargada
 * apareciera como gratis.
 */
function aColones(raw: string): number | null {
  const limpio = raw.trim();
  if (limpio === '') return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}
